import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ChevronDown,
  Home as HomeIcon,
  Calculator,
  BookOpen,
  Info,
  X,
} from "lucide-react";
import ThemeToggle from "./ThemeToggle.jsx";

/* ============================================================
   Sidebar
   ============================================================
   - Desktop (md+): permanent left sidebar 280px wide
   - Mobile (<md): drawer slides in, overlay closes on outside tap
   - Calculadoras and Guias are expandable sections
   - Auto-expands the section containing the current route
   ============================================================ */

const NAV_DATA = {
  calculadoras: [
    { to: "/calculadoras/salario", label: "Salário líquido" },
    { to: "/calculadoras/aposentadoria", label: "Dattai ichijikin" },
    { to: "/calculadoras/remessas", label: "Comparar remessas" },
  ],
  guias: [
    { to: "/guias/vale-a-pena-voltar", label: "Vale a pena voltar?" },
    { to: "/guias/quanto-ganha-operario-japao-2026", label: "Quanto ganha operário" },
    { to: "/guias/kakutei-shinkoku", label: "Kakutei shinkoku" },
    { to: "/guias/renovacao-visto-zairyu-card", label: "Renovar visto" },
    { to: "/guias/trazer-familia-para-o-japao", label: "Trazer família" },
    { to: "/guias/gaimen-kirikae", label: "Carteira de motorista" },
    { to: "/guias/abrir-conta-banco-japao", label: "Abrir conta no banco" },
    { to: "/guias/encontrar-moradia-apato-japao", label: "Encontrar apato" },
    { to: "/guias/hakken-vs-seishain", label: "Hakken vs seishain" },
    { to: "/guias/shakai-vs-kokumin", label: "Shakai vs kokumin" },
    { to: "/guias/mandar-dinheiro-para-o-brasil", label: "Mandar dinheiro" },
    { to: "/guias/trabalhar-legal-japao-tipos-de-visto", label: "Tipos de visto" },
    { to: "/guias/glossario-japones-portugues", label: "Glossário JP-PT" },
  ],
};

export default function Sidebar({ open, onClose }) {
  const { pathname } = useLocation();
  const [calcOpen, setCalcOpen] = useState(false);
  const [guiasOpen, setGuiasOpen] = useState(false);

  // Auto-expand the section containing the current route
  useEffect(() => {
    if (pathname.startsWith("/calculadoras")) setCalcOpen(true);
    if (pathname.startsWith("/guias")) setGuiasOpen(true);
  }, [pathname]);

  return (
    <>
      {/* Mobile overlay */}
      <div
        onClick={onClose}
        className={`md:hidden fixed inset-0 z-30 bg-ink/60 backdrop-blur-sm transition-opacity duration-200 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden="true"
      />

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-40 h-full w-[280px]
          bg-surface border-r border-border
          flex flex-col
          transform transition-transform duration-200 ease-out
          md:translate-x-0
          ${open ? "translate-x-0" : "-translate-x-full"}
        `}
      >
        {/* Header: Logo + close button (mobile) */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-border">
          <Link
            to="/"
            onClick={onClose}
            className="no-underline group"
          >
            <Wordmark />
          </Link>
          <button
            onClick={onClose}
            className="md:hidden p-1.5 rounded-lg text-ink-soft hover:text-ink hover:bg-border/40 transition-colors"
            aria-label="Fechar menu"
          >
            <X size={18} />
          </button>
        </div>

        {/* Nav links */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          <NavItem to="/" icon={<HomeIcon size={16} />} onClick={onClose} exact>
            Início
          </NavItem>

          <ExpandableSection
            label="Calculadoras"
            icon={<Calculator size={16} />}
            open={calcOpen}
            onToggle={() => setCalcOpen((o) => !o)}
            count={NAV_DATA.calculadoras.length}
          >
            {NAV_DATA.calculadoras.map((item) => (
              <NavChild key={item.to} to={item.to} onClick={onClose}>
                {item.label}
              </NavChild>
            ))}
          </ExpandableSection>

          <ExpandableSection
            label="Guias"
            icon={<BookOpen size={16} />}
            open={guiasOpen}
            onToggle={() => setGuiasOpen((o) => !o)}
            count={NAV_DATA.guias.length}
          >
            {NAV_DATA.guias.map((item) => (
              <NavChild key={item.to} to={item.to} onClick={onClose}>
                {item.label}
              </NavChild>
            ))}
          </ExpandableSection>

          <NavItem to="/sobre" icon={<Info size={16} />} onClick={onClose}>
            Sobre
          </NavItem>
        </nav>

        {/* Footer: theme toggle */}
        <div className="px-3 py-3 border-t border-border">
          <ThemeToggle />
          <div className="px-4 pt-3 text-[10px] font-mono text-ink-soft/70 tracking-wider">
            v0.6.0 · beta
          </div>
        </div>
      </aside>
    </>
  );
}

function Wordmark() {
  return (
    <div className="font-serif font-medium text-xl leading-tight text-ink tracking-tight">
      Aqui no{" "}
      <span className="italic font-semibold text-accent">Japão</span>
    </div>
  );
}

function NavItem({ to, icon, children, onClick, exact }) {
  const { pathname } = useLocation();
  const isActive = exact ? pathname === to : pathname === to || pathname.startsWith(to + "/");

  return (
    <Link
      to={to}
      onClick={onClick}
      className={`
        flex items-center gap-3 px-4 py-2.5 rounded-lg no-underline text-sm font-medium
        transition-colors mb-0.5
        ${
          isActive
            ? "bg-accent-soft text-accent"
            : "text-ink-soft hover:text-ink hover:bg-border/40"
        }
      `}
    >
      <span className={isActive ? "text-accent" : ""}>{icon}</span>
      {children}
    </Link>
  );
}

function ExpandableSection({
  label,
  icon,
  open,
  onToggle,
  count,
  children,
}) {
  const { pathname } = useLocation();
  // Section is "active" if any child route is active
  const hasActiveChild = pathname.startsWith("/" + label.toLowerCase());

  return (
    <div className="mb-0.5">
      <button
        onClick={onToggle}
        className={`
          w-full flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg text-sm font-medium
          transition-colors
          ${
            hasActiveChild && !open
              ? "text-accent"
              : "text-ink-soft hover:text-ink hover:bg-border/40"
          }
        `}
        aria-expanded={open}
      >
        <span className="flex items-center gap-3">
          <span className={hasActiveChild && !open ? "text-accent" : ""}>
            {icon}
          </span>
          {label}
        </span>
        <span className="flex items-center gap-2">
          {count !== undefined && (
            <span className="text-[10px] font-mono opacity-50">{count}</span>
          )}
          <ChevronDown
            size={14}
            className={`transition-transform duration-200 ${
              open ? "rotate-180" : ""
            }`}
          />
        </span>
      </button>
      <div
        className={`
          overflow-hidden transition-all duration-200 ease-out
          ${open ? "max-h-96 opacity-100 mt-1" : "max-h-0 opacity-0"}
        `}
      >
        <div className="ml-3 pl-3 border-l border-border space-y-0.5 py-1">
          {children}
        </div>
      </div>
    </div>
  );
}

function NavChild({ to, children, onClick }) {
  const { pathname } = useLocation();
  const isActive = pathname === to;

  return (
    <Link
      to={to}
      onClick={onClick}
      className={`
        block px-4 py-2 rounded-lg no-underline text-[13px]
        transition-colors
        ${
          isActive
            ? "bg-accent-soft text-accent font-semibold"
            : "text-ink-soft hover:text-ink hover:bg-border/40 font-medium"
        }
      `}
    >
      {children}
    </Link>
  );
}
